import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { PermissionsProvider } from "./contexts/PermissionsContext";
import { ToastProvider } from "./contexts/ToastContext";
import { ConfirmProvider } from "./contexts/ConfirmContext";
import LoginPage from "./pages/LoginPage";
import AdminLayout from "./components/AdminLayout";
import { isSuperAdminIdentity } from "./utils/jwtIdentity";

// Each page is its own download, fetched the first time it is opened, so the
// login screen does not wait for all ~45 pages (the bundle was 1.8 MB).
const DashboardPage = React.lazy(() => import("./pages/DashboardPage"));
const BookingsPage = React.lazy(() => import("./pages/BookingsPage"));
const CustomersPage = React.lazy(() => import("./pages/CustomersPage"));
const CustomerDetailsPage = React.lazy(() => import("./pages/CustomerDetailsPage"));
const CheckoutPage = React.lazy(() => import("./pages/CheckoutPage"));
const EventsPage = React.lazy(() => import("./pages/EventsPage"));
const EventPlannerPage = React.lazy(() => import("./pages/EventPlannerPage"));
const CreateEventPage = React.lazy(() => import("./pages/CreateEventPage"));
const EditEventPage = React.lazy(() => import("./pages/EditEventPage"));
const CreateQuotationPage = React.lazy(() => import("./pages/CreateQuotationPage"));
const QuotationsPage = React.lazy(() => import("./pages/QuotationsPage"));
const HallsPage = React.lazy(() => import("./pages/HallsPage"));
const RoomsPage = React.lazy(() => import("./pages/RoomsPage"));
const PackagesPage = React.lazy(() => import("./pages/PackagesPage"));
const RestaurantPage = React.lazy(() => import("./pages/RestaurantPage"));
const RestaurantMenuPage = React.lazy(() => import("./pages/RestaurantMenuPage"));
const EventTypesPage = React.lazy(() => import("./pages/EventTypesPage"));
const SettingsPage = React.lazy(() => import("./pages/SettingsPage"));
const UsersPage = React.lazy(() => import("./pages/UsersPage"));
const RolesPage = React.lazy(() => import("./pages/RolesPage"));
const BranchesPage = React.lazy(() => import("./pages/BranchesPage"));
const AmenitiesPage = React.lazy(() => import("./pages/AmenitiesPage"));
const BuildingsPage = React.lazy(() => import("./pages/BuildingsPage"));
const FloorsPage = React.lazy(() => import("./pages/FloorsPage"));
const FacilitiesPage = React.lazy(() => import("./pages/FacilitiesPage"));
const StatusMastersPage = React.lazy(() => import("./pages/StatusMastersPage"));
const FoodCategoriesPage = React.lazy(() => import("./pages/FoodCategoriesPage"));
const FoodMenusPage = React.lazy(() => import("./pages/FoodMenusPage"));
const PrinterSettingsPage = React.lazy(() => import("./pages/PrinterSettingsPage"));
const KotPage = React.lazy(() => import("./pages/KotPage"));
const FoodPackagesPage = React.lazy(() => import("./pages/FoodPackagesPage"));
const AdditionalServiceMastersPage = React.lazy(() => import("./pages/AdditionalServiceMastersPage"));
const KitchenTypesPage = React.lazy(() => import("./pages/KitchenTypesPage"));
const KitchensPage = React.lazy(() => import("./pages/KitchensPage"));
const KitchenCategoriesPage = React.lazy(() => import("./pages/KitchenCategoriesPage"));
const GuestServiceCategoriesPage = React.lazy(() => import("./pages/GuestServiceCategoriesPage"));
const GuestServiceItemsPage = React.lazy(() => import("./pages/GuestServiceItemsPage"));
const RatePlansPage = React.lazy(() => import("./pages/RatePlansPage"));
const ChannelManagerPage = React.lazy(() => import("./pages/ChannelManagerPage"));
const MealTypesPage = React.lazy(() => import("./pages/MealTypesPage"));
const MealPlansPage = React.lazy(() => import("./pages/MealPlansPage"));
const TaxServiceSettingsPage = React.lazy(() => import("./pages/TaxServiceSettingsPage"));
const TaxInvoicesPage = React.lazy(() => import("./pages/TaxInvoicesPage"));
const NewBookingPage = React.lazy(() => import("./pages/NewBookingPage"));
const RoomServicePage = React.lazy(() => import("./pages/RoomServicePage"));
const GuestRoomServicePage = React.lazy(() => import("./pages/GuestRoomServicePage"));
const HousekeepingPage = React.lazy(() => import("./pages/HousekeepingPage"));
const FoodHallPage = React.lazy(() => import("./pages/FoodHallPage"));
const HousekeepingTeamPage = React.lazy(() => import("./pages/HousekeepingTeamPage"));
const HousekeepingLoginPage = React.lazy(() => import("./pages/HousekeepingLoginPage"));
const ControlCenterPage = React.lazy(() => import("./pages/ControlCenterPage"));

export const PageLoading: React.FC = () => (
  <div className="flex items-center justify-center py-20 text-sm text-gray-500">Loading…</div>
);

// Protected Route Component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { isAuthenticated } = useAuth();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

// Main App Routes
const AppRoutes: React.FC = () => {
  const { isAuthenticated, user } = useAuth();

  const homeRedirect = !isAuthenticated
    ? "/login"
    : isSuperAdminIdentity(user!)
    ? "/control-center"
    : "/admin";

  return (
    <React.Suspense fallback={<PageLoading />}>
    <Routes>
      <Route
        path="/login"
        element={
          isAuthenticated ? <Navigate to={homeRedirect} replace /> : <LoginPage />
        }
      />

      {/* Guest QR app — no login */}
      <Route path="/rs/:roomId" element={<GuestRoomServicePage />} />
      <Route path="/guest/room-service" element={<GuestRoomServicePage />} />

      {/* Housekeeping team mobile app */}
      <Route path="/hk/login" element={<HousekeepingLoginPage />} />
      <Route path="/housekeeping/login" element={<HousekeepingLoginPage />} />
      <Route path="/hk" element={<HousekeepingTeamPage />} />
      <Route path="/housekeeping" element={<HousekeepingTeamPage />} />

      {/* Reseller Control Center — same login as the hotel admin; a SuperAdmin
          identity is routed here instead of /admin (see LoginPage/ProtectedRoute). */}
      <Route path="/control-center" element={<ControlCenterPage />} />

      <Route
        path="/admin"
        element={
          <ProtectedRoute>
            <AdminLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<DashboardPage />} />
        <Route path="bookings" element={<BookingsPage />} />
        <Route path="bookings/new" element={<NewBookingPage />} />
        <Route path="bookings/draft/:draftBookingId" element={<NewBookingPage />} />
        <Route
          path="bookings/edit/:directBookingId"
          element={<NewBookingPage />}
        />
        <Route path="customers" element={<CustomersPage />} />
        <Route path="customers/:customerId" element={<CustomerDetailsPage />} />
        <Route path="room-service" element={<RoomServicePage />} />
        {/* The admin housekeeping queue. The /hk mobile app above is the
            cleaner-facing half of the same workflow. */}
        <Route path="housekeeping" element={<HousekeepingPage />} />
        <Route
          path="checkout/:directBookingId/:roomBookingId"
          element={<CheckoutPage />}
        />
        <Route path="tax-invoices" element={<TaxInvoicesPage />} />
        <Route path="events" element={<EventsPage />} />
        <Route path="create-event" element={<CreateEventPage />} />
        {/* The day-wise planner (event redesign); the older wizard stays above until it is retired. */}
        <Route path="events/plan" element={<EventPlannerPage mode="event" />} />
        <Route path="events/:eventBookingId/plan" element={<EventPlannerPage mode="event" />} />
        <Route path="quotations/plan" element={<EventPlannerPage mode="quotation" />} />
        <Route path="quotations/:quotationId/plan" element={<EventPlannerPage mode="quotation" />} />
        <Route
          path="events/:eventBookingId/edit"
          element={<EditEventPage />}
        />
        <Route path="quotations" element={<QuotationsPage />} />
        <Route path="quotations/new" element={<CreateQuotationPage />} />
        <Route
          path="quotations/:quotationId/edit"
          element={<CreateQuotationPage />}
        />
        <Route path="halls" element={<HallsPage />} />
        <Route path="rooms" element={<RoomsPage />} />
        <Route path="packages" element={<PackagesPage />} />
        <Route path="restaurant" element={<RestaurantPage view="orders" />} />
        <Route path="restaurant-menu" element={<RestaurantMenuPage />} />
        <Route path="kot" element={<KotPage />} />
        {/* Room-wise guest counter for the food hall (breakfast/lunch/dinner). */}
        <Route path="food-hall" element={<FoodHallPage />} />
        <Route path="event-types" element={<EventTypesPage />} />
        <Route path="settings" element={<SettingsPage />} />
        <Route path="users" element={<UsersPage />} />
        <Route path="roles" element={<RolesPage />} />
        <Route path="branches" element={<BranchesPage />} />
        <Route path="amenities" element={<AmenitiesPage />} />
        <Route path="buildings" element={<BuildingsPage />} />
        <Route path="floors" element={<FloorsPage />} />
        <Route path="facilities" element={<FacilitiesPage />} />
        <Route path="status-masters" element={<StatusMastersPage />} />
        <Route path="kitchen-types" element={<KitchenTypesPage />} />
        <Route path="kitchens" element={<KitchensPage />} />
        <Route path="kitchen-categories" element={<KitchenCategoriesPage />} />
        <Route path="food-categories" element={<FoodCategoriesPage />} />
        <Route path="food-menus" element={<FoodMenusPage />} />
        <Route path="food-packages" element={<FoodPackagesPage />} />
        <Route path="additional-services" element={<AdditionalServiceMastersPage />} />
        <Route path="guest-service-categories" element={<GuestServiceCategoriesPage />} />
        <Route path="guest-service-items" element={<GuestServiceItemsPage />} />
        <Route path="rate-plans" element={<RatePlansPage />} />
        <Route path="channel-manager" element={<ChannelManagerPage />} />
        <Route path="meal-types" element={<MealTypesPage />} />
        <Route path="meal-plans" element={<MealPlansPage />} />
        <Route path="tax-service-settings" element={<TaxServiceSettingsPage />} />
        <Route path="printers" element={<PrinterSettingsPage />} />
      </Route>

      <Route path="/" element={<Navigate to={homeRedirect} replace />} />
      <Route path="*" element={<Navigate to={homeRedirect} replace />} />
    </Routes>
    </React.Suspense>
  );
};

// Main App Component
const App: React.FC = () => {
  return (
    <Router>
      <ToastProvider>
        <ConfirmProvider>
          <AuthProvider>
            <PermissionsProvider>
              <AppRoutes />
            </PermissionsProvider>
          </AuthProvider>
        </ConfirmProvider>
      </ToastProvider>
    </Router>
  );
};

export default App;
